import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Login.css';

function Login({ onLogin }) {
  const [authStatus, setAuthStatus] = useState({
    isLoading: false,
    error: null,
    timeoutReached: false,
    progress: 0
  });

  // Keep a stable ref to onLogin so the one-time mount effect can call it
  const onLoginRef = useRef(onLogin);
  useEffect(() => { onLoginRef.current = onLogin; });

  // Video / image states
  const [videoError, setVideoError] = useState(false);
  const [lockIconError, setLockIconError] = useState(false);
  const videoRef = useRef(null);

  // Typewriter states
  const [displayText, setDisplayText] = useState('');
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [isTyping, setIsTyping] = useState(true);
  const [showButtons, setShowButtons] = useState(false);

  const welcomeLines = [
  "Hi 👋",
  "I'm NIMI, your AI Assistant at NLNG.",
  "Ask me questions, explore data with me,\nand get things done faster.",
  "How can I help you today?"
  ];

  // Auto-play video once on mount
  useEffect(() => {
    if (videoRef.current && !videoError) {
      videoRef.current.loop = false;
      videoRef.current.play().catch(() => setVideoError(true));
    }
  }, [videoError]);

  // Typewriter effect
  useEffect(() => {
    const timeouts = [];
    if (currentLine < welcomeLines.length) {
      const line = welcomeLines[currentLine];
      if (currentChar < line.length) {
        const t = setTimeout(() => {
          setDisplayText(prev => prev + line[currentChar]);
          setCurrentChar(prev => prev + 1);
        }, 5);
        timeouts.push(t);
      } else {
        const t = setTimeout(() => {
          setDisplayText(prev => prev + '\n');
          setCurrentLine(prev => prev + 1);
          setCurrentChar(0);
        }, 200);
        timeouts.push(t);
      } 
    } else if (isTyping) {
      setIsTyping(false);
      setShowButtons(true);
    }
    return () => timeouts.forEach(t => clearTimeout(t));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLine, currentChar]);

  const ROOT_URL = window.location.origin;

  const APP_ID = 'app_2f72228f17c34321806c93cd04eee9b2';
  const APP_SECRET = 'PU3qtqxz6VrZqvm8kI2VCKh6MwI3WqJi+NrsUH+4U7g=';
//  const APP_ID = 'app_88fb3d98bec34aa2bfe7c22bc7fa3d57';
//  const APP_SECRET = 'ZvvHWQseMdd/6U+iWi1utfQkmDcjTtiUFGcKa19berQ=';
  const SSO_BASE = 'https://nlng-mail-service-prd-f5atf5b4afdgcman.westeurope-01.azurewebsites.net';

  const handleSSOLogin = useCallback(() => {
    const loginUrl = `${SSO_BASE}/cas/login?service=${encodeURIComponent(ROOT_URL)}&application=${encodeURIComponent(APP_ID)}&state=auth`;
    window.location.href = loginUrl;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On mount: check for SSO ticket in URL. If no ticket, wipe any stored
  // session so the green screen is always shown and re-login is required.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticket = params.get('ticket');
    const state = params.get('state');

    // If ticket is present, validate it
    if (ticket && state === 'auth') {
      setAuthStatus({
        isLoading: true,
        error: null,
        timeoutReached: false,
        progress: 60
      });
      const validateUrl = `${SSO_BASE}/cas/validate`;
      fetch(validateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticket,
          service: ROOT_URL,
          applicationId: APP_ID,
          applicationSecret: APP_SECRET
        })
      })
        .then(resp => resp.json())
        .then(validateData => {
          if (!validateData.isValid || !validateData.user) {
            throw new Error(validateData.errorMessage || 'SSO validation failed');
          }
          setAuthStatus(prev => ({ ...prev, progress: 100 }));
          const { userId, email, displayName } = validateData.user;
          localStorage.setItem('userId', userId);
          localStorage.setItem('email', email);
          localStorage.setItem('displayName', displayName);
          localStorage.setItem('loginTimestamp', Date.now().toString());
          // Clean up ticket from URL
          const url = new URL(window.location);
          url.searchParams.delete('ticket');
          url.searchParams.delete('state');
          window.history.replaceState({}, document.title, url.pathname + url.search);
          onLoginRef.current();
        })
        .catch(error => {
          setAuthStatus({
            isLoading: false,
            error: error.message || 'Authentication failed. No Access.',
            timeoutReached: false,
            progress: 0
          });
        });
      return;
    }

    // No SSO ticket — clear only expired session data
    const loginTimestamp = localStorage.getItem('loginTimestamp');
    const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
    if (loginTimestamp && Date.now() - parseInt(loginTimestamp, 10) >= FIVE_HOURS_MS) {
      localStorage.removeItem('loginTimestamp');
      localStorage.removeItem('userId');
      localStorage.removeItem('email');
      localStorage.removeItem('displayName');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run only once on mount; ROOT_URL = window.location.origin is stable

  if (authStatus.isLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Authenticating with NLNG SSO... {authStatus.progress}%</p>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${authStatus.progress}%` }}
          ></div>
        </div>
        {authStatus.timeoutReached && (
          <p className="error-message">No Access: Authentication timed out</p>
        )}
      </div>
    );
  }

  if (authStatus.error) {
    return (
      <div className="no-access-screen">
        <div className="no-access-content">
          <h2>No Access</h2>
          <p className="error-message">{authStatus.error}</p>
          <button 
            className="retry-button" 
            onClick={handleSSOLogin}
          >
            Retry Authentication
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="nimi-sso-container">
      {/* LEFT SIDE - VIDEO/IMAGE */}
      <div className="nimi-left-panel">
        {!videoError ? (
          <video
            ref={videoRef}
            className="nimi-avatar-video"
            src="/NIMI_Avatar.mp4"
            onError={() => setVideoError(true)}
            onEnded={() => { if (videoRef.current) videoRef.current.pause(); }}
            muted
            playsInline
            autoPlay
            aria-label="NIMI AI Assistant avatar animation"
          />
        ) : (
          <img
            src="/Chat_Circle_Dots.png"
            alt="NIMI AI Assistant"
            className="nimi-avatar-image"
          />
        )}
      </div>

      {/* RIGHT SIDE - TYPEWRITER + BUTTONS */}
      <div className="nimi-right-panel">
        <div className="nimi-welcome-content">
          <div className="typewriter-container">
            <pre className="typewriter-text" aria-live="polite" aria-label="Welcome message">
              {displayText}
              {isTyping && <span className="cursor" aria-hidden="true">|</span>}
            </pre>
          </div>

          {showButtons && (
            <>
              <div className="nimi-chat-btn-container">
                <button
                  className="nimi-chat-btn"
                  onClick={handleSSOLogin}
                  disabled={authStatus.isLoading}
                  aria-label="Login with SSO to start chatting"
                >
                  <span>Let's Chat</span>
                  <img
                    src="/Chat_Circle_Dots.png"
                    alt="chat icon"
                    className="chat-btn-icon"
                  />
                </button>
              </div>

              <div
                className="nimi-sso-text"
              >
                {!lockIconError ? (
                  <img
                    src="/lock.png"
                    alt="lock icon"
                    className="sso-icon lock-icon"
                    onError={() => setLockIconError(true)}
                  />
                ) : (
                  <span className="icon-fallback">🔒</span>
                )}
                <span>Secure SSO Login</span>
              </div>

              <footer className="nimi-footer">
                <p className="nimi-copyright">
                  © {new Date().getFullYear()} Nigeria LNG Limited. All rights reserved.
                </p>
                <p className="nimi-credits">
                  Developed by CSD Department. Protected by Enterprise Security Protocols.
                </p>
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;